import { apiClient } from '../publicApiClient';

/** First-party Growth touch (no Meta cookies). */
export type AttributionTouch = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  landing_page?: string;
  referrer?: string;
};

/** Stored in localStorage; `capturedAt` is client-only (not sent to the API). */
type StoredTouch = AttributionTouch & { capturedAt?: number };

/**
 * Booking requests only include attribution fresher than this.
 * Registration / profile complete send stored attribution with no age cap.
 */
export const BOOKING_ATTRIBUTION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type AnalyticsPayloadPurpose = 'registration' | 'booking';

/** Optional envelope on auth / booking / profile conversion requests. */
export type AnalyticsPayload = {
  attribution?: AttributionTouch;
  ga4?: { clientId?: string };
  meta?: { fbp?: string; fbc?: string };
};

/** @deprecated Use AttributionTouch / AnalyticsPayload. */
export type BookingAttributionPayload = AttributionTouch;

export type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
};

const TOUCH_KEY = 'bs_attr_touch';
/** @deprecated Migrated into {@link TOUCH_KEY}. */
const LEGACY_FIRST_KEY = 'bs_attr_first';
/** @deprecated Migrated into {@link TOUCH_KEY}. */
const LEGACY_LAST_KEY = 'bs_attr_last';
const CLIENT_ID_KEY = 'bs_ga_client_id';
const CONSENT_KEY = 'cookiePreferences';
const CONSENT_DECIDED_KEY = 'cookiePreferencesDecided';
const FUNNEL_SESSION_KEY = 'bs_booking_funnel_session';

export const META_PIXEL_ID = '1636973557961695';

const ATTR_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'gclid',
  'fbclid',
  'ttclid',
] as const;

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  loaded?: boolean;
  version?: string;
  push: Fbq;
};

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

export function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/';
}

/**
 * GTM / Meta Pixel / browser gtag publishing — prod builds only.
 * First-party attribution still runs in local/test.
 */
export function isExternalAnalyticsEnabled(): boolean {
  if (typeof process !== 'undefined' && process.env.REACT_APP_EXTERNAL_ANALYTICS_ENABLED === 'true') {
    return true;
  }
  if (typeof window !== 'undefined' && (window as Window & { __BS_EXTERNAL_ANALYTICS__?: boolean }).__BS_EXTERNAL_ANALYTICS__ === true) {
    return true;
  }
  return false;
}

/** Ad destinations in the app — Pixel may load here before a consent decision. */
export function isAdFunnelPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return (
    path === '/invite/user' ||
    path === '/invite/host' ||
    path === '/invite/tutor' ||
    path === '/invite/user/complete' ||
    path === '/invite/host/complete' ||
    path === '/invite/tutor/complete' ||
    path === '/verify-code' ||
    path === '/complete-profile' ||
    path === '/login' ||
    path === '/onboarding/plan' ||
    path.startsWith('/user/search') ||
    path.startsWith('/spaces/')
  );
}

export function shouldLoadMetaPixel(
  pathname = typeof window !== 'undefined' ? window.location.pathname : '/',
): boolean {
  if (!isExternalAnalyticsEnabled()) return false;
  if (typeof window === 'undefined') return isAdFunnelPath(pathname);
  const decided = hasDecidedCookiePreferences();
  const prefs = getCookiePreferences();
  if (decided) return !!prefs.marketing;
  return isAdFunnelPath(pathname);
}

/** Purchase / Lead / etc. fire unless marketing was explicitly declined. */
export function shouldFireMetaConversion(): boolean {
  if (!isExternalAnalyticsEnabled()) return false;
  if (typeof window === 'undefined') return false;
  if (!hasDecidedCookiePreferences()) return true;
  return !!getCookiePreferences().marketing;
}

function createEventId(prefix: string): string {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}.${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}_${id}`;
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

export function getMetaClickIds(): { fbp?: string; fbc?: string } {
  const fbp = readCookie('_fbp');
  const cookieFbc = readCookie('_fbc');
  if (cookieFbc) return { fbp, fbc: cookieFbc };

  const stored = typeof window !== 'undefined' ? readStoredTouch() : undefined;
  const fbclid =
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('fbclid')
      : null) ||
    stored?.fbclid;
  if (!fbclid) return { fbp };
  return { fbp, fbc: `fb.1.${Date.now()}.${fbclid}` };
}

/** Official Meta Pixel loader. PageView is sent once on first init. */
export function initMetaPixel(options?: { forConversion?: boolean }): void {
  if (typeof window === 'undefined') return;
  const allowed = options?.forConversion ? shouldFireMetaConversion() : shouldLoadMetaPixel();
  if (!allowed) return;
  if (window.fbq) {
    return;
  }

  const n = function (...args: unknown[]) {
    if (n.callMethod) n.callMethod(...args);
    else n.queue.push(args);
  } as Fbq;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  window.fbq = n;
  window._fbq = n;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const first = document.getElementsByTagName('script')[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head?.appendChild(script);
  }

  n('init', META_PIXEL_ID);
  n('track', 'PageView');
}

function trackMetaEvent(
  event: string,
  params: Record<string, unknown> = {},
  eventId?: string,
): string | undefined {
  if (typeof window === 'undefined' || !shouldFireMetaConversion()) return undefined;
  initMetaPixel({ forConversion: true });
  if (!window.fbq) return undefined;
  const id = eventId || createEventId(event.toLowerCase());
  window.fbq('track', event, params, { eventID: id });
  return id;
}

function trackMetaCustomEvent(
  event: string,
  params: Record<string, unknown> = {},
  eventId?: string,
): string | undefined {
  if (typeof window === 'undefined' || !shouldFireMetaConversion()) return undefined;
  initMetaPixel({ forConversion: true });
  if (!window.fbq) return undefined;
  const id = eventId || createEventId(event.toLowerCase());
  window.fbq('trackCustom', event, params, { eventID: id });
  return id;
}

export function trackMetaPageView(): void {
  if (typeof window === 'undefined' || !shouldLoadMetaPixel()) return;
  if (!window.fbq) {
    initMetaPixel();
    return;
  }
  window.fbq('track', 'PageView');
}

export function getCookiePreferences(): CookiePreferences {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (raw) {
      return { necessary: true, analytics: false, marketing: false, preferences: false, ...JSON.parse(raw) };
    }
  } catch {
    // ignore
  }
  return { necessary: true, analytics: false, marketing: false, preferences: false };
}

export function hasDecidedCookiePreferences(): boolean {
  return localStorage.getItem(CONSENT_DECIDED_KEY) === '1' || !!localStorage.getItem(CONSENT_KEY);
}

export function saveCookiePreferences(prefs: CookiePreferences): void {
  const next = { ...prefs, necessary: true };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(next));
    localStorage.setItem(CONSENT_DECIDED_KEY, '1');
  } catch (error) {
    console.error('Failed to persist cookie preferences', error);
    throw error;
  }
  try {
    applyGoogleConsent(next);
    if (next.marketing) {
      initMetaPixel();
    }
    pushDataLayerEvent('cookie_consent_update', {
      analytics: next.analytics,
      marketing: next.marketing,
      preferences: next.preferences,
    });
  } catch (error) {
    console.error('Failed to apply Google consent update', error);
  }
}

export function applyGoogleConsent(prefs: CookiePreferences = getCookiePreferences()): void {
  if (!isExternalAnalyticsEnabled()) return;
  const analytics = prefs.analytics ? 'granted' : 'denied';
  const marketing = prefs.marketing ? 'granted' : 'denied';
  window.dataLayer = window.dataLayer || [];
  const gtag = window.gtag || ((...args: unknown[]) => {
    window.dataLayer!.push(args as unknown as Record<string, unknown>);
  });
  window.gtag = gtag;
  gtag('consent', 'update', {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  });
}

/** Call before GTM loads. */
export function initGoogleConsentDefaults(): void {
  if (!isExternalAnalyticsEnabled()) return;
  window.dataLayer = window.dataLayer || [];
  const gtag = (...args: unknown[]) => {
    window.dataLayer!.push(args as unknown as Record<string, unknown>);
  };
  window.gtag = gtag;
  const decided = hasDecidedCookiePreferences();
  const prefs = getCookiePreferences();
  gtag('consent', 'default', {
    analytics_storage: decided && prefs.analytics ? 'granted' : 'denied',
    ad_storage: decided && prefs.marketing ? 'granted' : 'denied',
    ad_user_data: decided && prefs.marketing ? 'granted' : 'denied',
    ad_personalization: decided && prefs.marketing ? 'granted' : 'denied',
    wait_for_update: 500,
  });
}

export function getGaClientId(): string {
  try {
    const existing = localStorage.getItem(CLIENT_ID_KEY);
    if (existing) return existing;
    const id = `${Date.now()}.${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(CLIENT_ID_KEY, id);
    return id;
  } catch {
    return `${Date.now()}.anon`;
  }
}

function readRawTouch(key: string): StoredTouch | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    return JSON.parse(raw) as StoredTouch;
  } catch {
    return undefined;
  }
}

function withCapturedAt(touch: StoredTouch): StoredTouch {
  if (typeof touch.capturedAt === 'number') return touch;
  // Legacy rows without capturedAt: start a fresh 7d booking window from first read.
  return { ...touch, capturedAt: Date.now() };
}

/** Single stored marketing touch (migrates legacy first/last keys). */
function readStoredTouch(): StoredTouch | undefined {
  try {
    const current = readRawTouch(TOUCH_KEY);
    if (current) {
      const hydrated = withCapturedAt(current);
      if (hydrated !== current) {
        localStorage.setItem(TOUCH_KEY, JSON.stringify(hydrated));
      }
      return hydrated;
    }

    const legacy = readRawTouch(LEGACY_LAST_KEY) || readRawTouch(LEGACY_FIRST_KEY);
    if (!legacy) return undefined;

    const migrated = withCapturedAt(legacy);
    localStorage.setItem(TOUCH_KEY, JSON.stringify(migrated));
    localStorage.removeItem(LEGACY_FIRST_KEY);
    localStorage.removeItem(LEGACY_LAST_KEY);
    return migrated;
  } catch {
    return undefined;
  }
}

function writeStoredTouch(touch: AttributionTouch): void {
  const stored: StoredTouch = { ...touch, capturedAt: Date.now() };
  localStorage.setItem(TOUCH_KEY, JSON.stringify(stored));
  try {
    localStorage.removeItem(LEGACY_FIRST_KEY);
    localStorage.removeItem(LEGACY_LAST_KEY);
  } catch {
    // ignore
  }
}

function isTouchWithinMaxAge(touch: StoredTouch | undefined, maxAgeMs: number | undefined): boolean {
  if (!touch) return false;
  if (maxAgeMs == null) return true;
  const capturedAt = typeof touch.capturedAt === 'number' ? touch.capturedAt : undefined;
  if (capturedAt == null) return false;
  return Date.now() - capturedAt <= maxAgeMs;
}

function referrerHostname(referrer?: string): string | undefined {
  if (!referrer) return undefined;
  try {
    return new URL(referrer).hostname.toLowerCase().replace(/^www\./, '') || undefined;
  } catch {
    return undefined;
  }
}

function isBandSpaceHostname(hostname?: string): boolean {
  return !!hostname && (hostname === 'band-space.com' || hostname.endsWith('.band-space.com'));
}

function isLocalDevHostname(hostname?: string): boolean {
  if (!hostname) return false;
  const normalized = hostname.toLowerCase().replace(/^www\./, '');
  return (
    normalized === 'localhost' ||
    normalized === '127.0.0.1' ||
    normalized === '0.0.0.0' ||
    normalized === '::1' ||
    normalized === '[::1]' ||
    normalized.endsWith('.localhost') ||
    normalized.endsWith('.local')
  );
}

function sanitizeStoredReferrer(referrer?: string): string | undefined {
  if (!referrer) return undefined;
  const hostname = referrerHostname(referrer);
  if (hostname && isLocalDevHostname(hostname)) return undefined;
  const lower = referrer.toLowerCase();
  if (
    lower.includes('localhost') ||
    lower.includes('127.0.0.1') ||
    lower.includes('[::1]') ||
    lower.includes('://0.0.0.0')
  ) {
    return undefined;
  }
  return referrer;
}

function canonicalSocialSource(source: string): string {
  const normalized = source.toLowerCase();
  if (normalized === 'ig' || normalized === 'instagram' || normalized.endsWith('.instagram.com')) return 'instagram';
  if (['fb', 'meta', 'facebook'].includes(normalized) || normalized.endsWith('.facebook.com')) return 'facebook';
  if (normalized === 'threads' || normalized === 'threads.net' || normalized.endsWith('.threads.net')) return 'threads';
  if (normalized === 'tiktok' || normalized === 'tiktok.com' || normalized.endsWith('.tiktok.com')) return 'tiktok';
  return normalized;
}

/** Fill a missing/synthetic source without overwriting explicit campaign UTMs. */
function normalizeTouch(touch: AttributionTouch): AttributionTouch {
  const normalized = { ...touch };
  const source = normalized.utm_source?.trim().toLowerCase();
  if (source) normalized.utm_source = canonicalSocialSource(source);
  const syntheticSource = !source || source === 'direct' || source === 'referral';
  const hostname = referrerHostname(normalized.referrer);

  if (syntheticSource && normalized.gclid) {
    normalized.utm_source = 'google';
    normalized.utm_medium = 'cpc';
  } else if (syntheticSource && normalized.fbclid) {
    normalized.utm_source = 'facebook';
    normalized.utm_medium = 'social';
  } else if (syntheticSource && normalized.ttclid) {
    normalized.utm_source = 'tiktok';
    normalized.utm_medium = 'social';
  } else if (syntheticSource && hostname && !isBandSpaceHostname(hostname) && !isLocalDevHostname(hostname)) {
    normalized.utm_source = canonicalSocialSource(hostname);
    normalized.utm_medium = 'referral';
  } else if (syntheticSource) {
    normalized.utm_source = 'direct';
    normalized.utm_medium = 'none';
  }

  if (normalized.referrer) {
    normalized.referrer = sanitizeStoredReferrer(normalized.referrer);
  }
  if (normalized.landing_page && /localhost|127\.0\.0\.1|\[::1\]|0\.0\.0\.0/i.test(normalized.landing_page)) {
    delete normalized.landing_page;
  }

  return normalized;
}

function touchHasSignal(touch: AttributionTouch): boolean {
  return ATTR_PARAMS.some((k) => !!touch[k]);
}

function parseTouchFromLocation(search: string, pathname: string): AttributionTouch {
  const params = new URLSearchParams(search);
  const touch: AttributionTouch = {};
  for (const key of ATTR_PARAMS) {
    const value = params.get(key);
    if (value) touch[key] = value;
  }
  touch.landing_page = `${pathname}${search}` || '/';
  if (typeof document !== 'undefined' && document.referrer) {
    touch.referrer = sanitizeStoredReferrer(document.referrer);
  }
  return touch;
}

/** Capture/overwrite the single stored touch from the current URL. */
export function captureAttributionFromLocation(
  search = window.location.search,
  pathname = window.location.pathname,
): void {
  const touch = parseTouchFromLocation(search, pathname);
  if (!touchHasSignal(touch) && !readStoredTouch()) {
    // Still record first landing page for organic/direct
    const organic = normalizeTouch({
      landing_page: touch.landing_page,
      referrer: touch.referrer,
    });
    writeStoredTouch(organic);
    return;
  }
  if (!touchHasSignal(touch)) return;

  const prefs = getCookiePreferences();
  if (!prefs.marketing) {
    delete touch.gclid;
    delete touch.fbclid;
    delete touch.ttclid;
  }

  writeStoredTouch(normalizeTouch(touch));
}

function toApiAttribution(touch: AttributionTouch | undefined): AttributionTouch | undefined {
  if (!touch) return undefined;
  const normalized = normalizeTouch(touch);
  const {
    utm_source,
    utm_medium,
    utm_campaign,
    gclid,
    fbclid,
    ttclid,
    landing_page,
    referrer,
  } = normalized;
  const attribution: AttributionTouch = {
    ...(utm_source ? { utm_source } : {}),
    ...(utm_medium ? { utm_medium } : {}),
    ...(utm_campaign ? { utm_campaign } : {}),
    ...(gclid ? { gclid } : {}),
    ...(fbclid ? { fbclid } : {}),
    ...(ttclid ? { ttclid } : {}),
    ...(landing_page ? { landing_page } : {}),
    ...(referrer ? { referrer } : {}),
  };
  return Object.keys(attribution).length > 0 ? attribution : undefined;
}

/**
 * Current stored touch for the API.
 * @param maxAgeMs when set, omit attribution older than this (booking lookback).
 */
export function getAttributionPayload(maxAgeMs?: number): AttributionTouch | undefined {
  const stored = readStoredTouch();
  if (!isTouchWithinMaxAge(stored, maxAgeMs)) return undefined;
  return toApiAttribution(stored);
}

/**
 * Full `analytics` envelope for conversion requests.
 * - `registration` (default): send stored attribution with no age cap (user acquisition).
 * - `booking`: send attribution only if captured within {@link BOOKING_ATTRIBUTION_MAX_AGE_MS}.
 * `meta` is omitted outside prod and when marketing consent is declined.
 */
export function getAnalyticsPayload(purpose: AnalyticsPayloadPurpose = 'registration'): AnalyticsPayload | undefined {
  const maxAgeMs = purpose === 'booking' ? BOOKING_ATTRIBUTION_MAX_AGE_MS : undefined;
  const attribution = getAttributionPayload(maxAgeMs);
  const ga4 = { clientId: getGaClientId() };
  let meta: AnalyticsPayload['meta'];
  // Do not send Meta match keys outside prod — avoids CAPI even if the API flag is mis-set.
  if (isExternalAnalyticsEnabled() && shouldFireMetaConversion()) {
    const ids = getMetaClickIds();
    if (ids.fbp || ids.fbc) {
      meta = {
        ...(ids.fbp ? { fbp: ids.fbp } : {}),
        ...(ids.fbc ? { fbc: ids.fbc } : {}),
      };
    } else {
      meta = {};
    }
  }
  if (!attribution && !meta) {
    return { ga4 };
  }
  return {
    ...(attribution ? { attribution } : {}),
    ga4,
    ...(meta ? { meta } : {}),
  };
}

/** Booking create envelope — 7-day attribution lookback. */
export function getBookingAnalyticsPayload(): AnalyticsPayload | undefined {
  return getAnalyticsPayload('booking');
}

export function pushDataLayerEvent(
  event: string,
  params: Record<string, unknown> = {},
): void {
  if (!isExternalAnalyticsEnabled()) return;
  const prefs = getCookiePreferences();
  if (!prefs.analytics && event !== 'cookie_consent_update') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    ...params,
  });
  if (event === 'cookie_consent_update') return;
  // GTM only installs the GA4 Google tag. Custom dataLayer events are not
  // forwarded unless they are also sent as gtag events.
  const gtag = window.gtag || ((...args: unknown[]) => {
    window.dataLayer!.push(args as unknown as Record<string, unknown>);
  });
  window.gtag = gtag;
  gtag('event', event, params);
}

function bookingContentCategory(providerType: 'space' | 'tutor'): string {
  return providerType === 'tutor' ? 'tutor_lesson' : 'rehearsal_room';
}

/** Listed room price for the slot the user is about to book. */
export function estimateHourlyBookingValue(
  pricePerHour: number | null | undefined,
  durationMinutes: number | null | undefined,
): number | undefined {
  if (pricePerHour == null || !Number.isFinite(pricePerHour)) return undefined;
  if (durationMinutes == null || !Number.isFinite(durationMinutes) || durationMinutes <= 0) return undefined;
  return Math.round(pricePerHour * (durationMinutes / 60) * 100) / 100;
}

export function trackPageView(location: { pathname: string; search: string; hash: string }): void {
  captureAttributionFromLocation(location.search, location.pathname);
  pushDataLayerEvent('page_view', {
    page_path: location.pathname + location.search + location.hash,
    page_location: window.location.href,
    page_title: document.title,
  });
  trackMetaPageView();
}

export function trackSearch(params: {
  city?: string;
  search_term?: string;
  results_count?: number;
}): void {
  const eventId = createEventId('search');
  const searchTerm = params.search_term?.trim() || params.city;
  pushDataLayerEvent('search', {
    event_id: eventId,
    search_term: searchTerm,
    ...params,
  });
  trackMetaEvent(
    'Search',
    {
      content_category: 'rehearsal_room',
      ...(searchTerm ? { search_string: searchTerm } : {}),
      ...(params.city ? { city: params.city } : {}),
      ...(params.results_count != null ? { num_items: params.results_count } : {}),
    },
    eventId,
  );
}

export function trackViewProvider(params: {
  provider_type: 'space' | 'tutor';
  provider_id: string;
  city?: string;
  provider_name?: string;
  /** Starting hourly price when known. */
  value?: number;
  currency?: string;
}): void {
  const eventId = createEventId('viewcontent');
  pushDataLayerEvent('view_provider', {
    event_id: eventId,
    ...params,
  });
  trackMetaEvent(
    'ViewContent',
    {
      content_category: bookingContentCategory(params.provider_type),
      content_ids: [params.provider_id],
      content_type: 'product',
      ...(params.provider_name ? { content_name: params.provider_name } : {}),
      ...(params.city ? { city: params.city } : {}),
      ...(params.currency ? { currency: params.currency } : {}),
      ...(params.value != null ? { value: params.value } : {}),
    },
    eventId,
  );
}

export function trackStartBooking(params: {
  provider_type: 'space' | 'tutor';
  provider_id: string;
  city?: string;
  booking_value?: number;
  currency?: string;
}): void {
  const eventId = createEventId('initiatecheckout');
  pushDataLayerEvent('start_booking', {
    currency: 'PLN',
    event_id: eventId,
    ...params,
  });
  trackMetaEvent(
    'InitiateCheckout',
    {
      content_category: bookingContentCategory(params.provider_type),
      content_ids: [params.provider_id],
      currency: params.currency || 'PLN',
      ...(params.booking_value != null ? { value: params.booking_value } : {}),
      ...(params.city ? { city: params.city } : {}),
    },
    eventId,
  );
}

/**
 * User is looking at a booking stepper step.
 * Fires for the first step when the dialog opens, and again on each later step.
 */
export function trackBookingStep(params: {
  provider_type: 'space' | 'tutor';
  provider_id: string;
  step: string;
  step_index?: number;
  city?: string;
}): void {
  const eventId = createEventId('bookingstep');
  pushDataLayerEvent('booking_step', {
    event_id: eventId,
    ...params,
  });
  trackMetaCustomEvent(
    'BookingStep',
    {
      content_category: bookingContentCategory(params.provider_type),
      content_ids: [params.provider_id],
      content_name: params.step,
      ...(params.city ? { city: params.city } : {}),
      ...(params.step_index != null ? { step_index: params.step_index } : {}),
    },
    eventId,
  );
}

/** User hit Book but must log in before the booking can be created. */
export function trackLoginForBooking(params: {
  provider_type: 'space' | 'tutor';
  provider_id: string;
  city?: string;
}): void {
  const eventId = createEventId('loginforbooking');
  pushDataLayerEvent('login_for_booking', {
    event_id: eventId,
    ...params,
  });
  trackMetaCustomEvent(
    'LoginForBooking',
    {
      content_category: bookingContentCategory(params.provider_type),
      content_ids: [params.provider_id],
      ...(params.city ? { city: params.city } : {}),
    },
    eventId,
  );
}

/**
 * Booking row created successfully (cash/online/pass). Payment is optional —
 * this is the primary marketing conversion for the book funnel.
 */
export function trackBookingConfirmed(params: {
  provider_type: 'space' | 'tutor';
  provider_id: string;
  booking_id: string;
  city?: string;
  booking_value?: number;
  currency?: string;
  payment_method?: string;
}): void {
  const eventId = params.booking_id ? `purchase_${params.booking_id}` : createEventId('purchase');
  pushDataLayerEvent('booking_confirmed', {
    currency: 'PLN',
    event_id: eventId,
    ...params,
  });
  trackMetaEvent(
    'Purchase',
    {
      content_category: bookingContentCategory(params.provider_type),
      content_ids: [params.booking_id],
      content_type: 'product',
      currency: params.currency || 'PLN',
      ...(params.booking_value != null ? { value: params.booking_value } : {}),
      ...(params.city ? { city: params.city } : {}),
      ...(params.payment_method ? { payment_method: params.payment_method } : {}),
    },
    eventId,
  );
}

/** Shared login form, or a role-specific invite signup. */
export type SignupAudience = 'account' | 'user' | 'host' | 'tutor';

function signupContentCategory(role: SignupAudience): string {
  return role === 'account' ? 'account_registration' : `${role}_registration`;
}

function signupSendCodeName(role: SignupAudience): string {
  return role === 'account' ? 'login_send_code' : `${role}_send_code`;
}

function signupOtpEventName(role: SignupAudience): string {
  switch (role) {
    case 'tutor':
      return 'TutorOtpVerified';
    case 'host':
      return 'HostOtpVerified';
    case 'user':
      return 'UserOtpVerified';
    case 'account':
      return 'SignupOtpVerified';
  }
}

/** Send-code succeeded. `account` is the shared /login form. */
export function trackSignupStarted(
  role: SignupAudience,
  params: { phone_country?: string } = {},
): void {
  const eventId = createEventId('lead');
  pushDataLayerEvent(role === 'tutor' ? 'tutor_signup_started' : 'signup_started', {
    event_id: eventId,
    role,
    ...params,
  });
  trackMetaEvent(
    'Lead',
    {
      content_category: signupContentCategory(role),
      content_name: signupSendCodeName(role),
      ...params,
    },
    eventId,
  );
}

/** OTP verified for a signup. `account` means a new /login user who still must pick a role. */
export function trackSignupOtpVerified(role: SignupAudience): void {
  const eventId = createEventId(role === 'tutor' ? 'tutorotpverified' : `${role}otpverified`);
  pushDataLayerEvent(role === 'tutor' ? 'tutor_otp_verified' : 'signup_otp_verified', {
    event_id: eventId,
    role,
  });
  trackMetaCustomEvent(
    signupOtpEventName(role),
    { content_category: signupContentCategory(role) },
    eventId,
  );
}

/** Profile created for musician, host, or tutor. */
export function trackRegistrationComplete(
  role: Exclude<SignupAudience, 'account'>,
  params: { user_id?: string } = {},
): void {
  const storedUserId =
    params.user_id ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('user_id') || undefined : undefined);
  const eventId = storedUserId
    ? `completeregistration_${storedUserId}_${role}`
    : createEventId('completeregistration');
  pushDataLayerEvent(role === 'tutor' ? 'tutor_registration_complete' : 'registration_complete', {
    event_id: eventId,
    role,
    ...params,
  });
  trackMetaEvent(
    'CompleteRegistration',
    {
      content_category: `${role}_registration`,
      content_name: role === 'tutor' ? 'tutor_profile_complete' : `${role}_profile_complete`,
      status: 'complete',
      ...params,
    },
    eventId,
  );
}

/** /invite/tutor Send Code succeeded — top of teacher signup funnel. */
export function trackTutorSignupStarted(params: { phone_country?: string } = {}): void {
  trackSignupStarted('tutor', params);
}

/** OTP verified on the teacher invite path. */
export function trackTutorOtpVerified(): void {
  trackSignupOtpVerified('tutor');
}

/** Teacher account created after /public/auth/tutor/profile/complete. */
export function trackTutorRegistrationComplete(params: { user_id?: string } = {}): void {
  trackRegistrationComplete('tutor', params);
}

/** Closed booking UI without creating a booking (drop-off after start_booking). */
export function trackAbandonBooking(params: {
  provider_type: 'space' | 'tutor';
  provider_id: string;
  city?: string;
  step?: string;
  step_index?: number;
}): void {
  const eventId = createEventId('abandonbooking');
  pushDataLayerEvent('abandon_booking', {
    event_id: eventId,
    ...params,
  });
  trackMetaCustomEvent(
    'AbandonBooking',
    {
      content_category: bookingContentCategory(params.provider_type),
      content_ids: [params.provider_id],
      ...(params.city ? { city: params.city } : {}),
      ...(params.step ? { content_name: params.step } : {}),
    },
    eventId,
  );
  if (params.step) {
    let sessionId = sessionStorage.getItem(FUNNEL_SESSION_KEY);
    if (!sessionId) {
      sessionId = typeof window.crypto !== 'undefined' && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(FUNNEL_SESSION_KEY, sessionId);
    }
    void apiClient.trackEvent('BOOKING_ABANDONED', {
      sessionId,
      providerType: params.provider_type,
      providerId: params.provider_id,
      step: params.step,
      ...(params.step_index != null ? { stepIndex: params.step_index } : {}),
    });
  }
}

/** Append stored UTM params to an absolute app URL. */
export function appendAttributionToUrl(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    const touch = readStoredTouch();
    if (!touch) return url;
    for (const key of ATTR_PARAMS) {
      const value = touch[key];
      if (value && !u.searchParams.has(key)) {
        u.searchParams.set(key, value);
      }
    }
    return u.toString();
  } catch {
    return url;
  }
}
